'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, MB, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/nn-extra'

const ConvExplorer = dynamic(() => import('../_components/widgets/NNExtraWidgets').then(m => m.ConvExplorer), { ssr: false })

export default function Convolutions() {
  return (
    <Lesson>
      <Step>
        <H>Networks that understand pictures</H>
        <P>
          An ordinary layer connects every pixel to every neuron. For images that’s wasteful. A 1 000 × 1 000 photo would need a
          million weights per neuron. And it ignores two facts about pictures: nearby pixels belong together, and a cat is a cat
          wherever it sits in the frame.
        </P>
        <P>
          A <strong>convolution</strong> uses both facts. It slides one small grid of weights, a <strong>kernel</strong>, across
          the whole image. At each position it takes the dot product of the kernel with the patch under it. The result is a new
          image, a <strong>feature map</strong>, that lights up wherever the patch looks like the kernel.
        </P>
        <Figure n="7.11" wide caption="Pick a kernel, then hover over the input (or press Scan). Each output pixel is the dot product of the kernel with the 3×3 patch under the orange square.">
          <ConvExplorer />
        </Figure>
      </Step>

      <Step gate={['q-params', 'conv2d']}>
        <H>Few weights, used everywhere</H>
        <MB>{'\\text{out}[i][j] = \\sum_{a}\\sum_{b} \\text{img}[i + a][j + b] \\cdot k[a][b]'}</MB>
        <Quiz
          id="q-params"
          question="A convolution layer with 16 kernels of size 3×3 runs over a 1 000 × 1 000 image. How many weights does it have (ignoring biases)?"
          options={[
            { label: '16 × 9 = 144', correct: true },
            { label: '16 million', why: 'That’s what a fully connected layer would need per output pixel. A kernel reuses its 9 weights at every position.' },
            { label: '1 000 000', why: 'The image size doesn’t matter: the same 9 weights slide everywhere.' },
          ]}
          explain="Weight sharing is what makes convolutions cheap and data-efficient: an edge detector learned in one corner works in every corner."
        />
        <Exercise
          id="conv2d"
          title="conv2d and maxPool"
          {...EX.conv2d}
          hints={[
            <>
              The output is <C>{'(H − kh + 1) × (W − kw + 1)'}</C>. Four nested loops: output row, output column, kernel row,
              kernel column.
            </>,
            <>
              maxPool: step <C>i</C> and <C>j</C> by <C>size</C>, and keep the biggest value in each block.
            </>,
          ]}
        />
        <Callout kind="plain">
          <strong>Pooling</strong> shrinks a feature map by keeping only the strongest response in each block. The network
          stops caring exactly where a feature was, as long as it was roughly there.
        </Callout>
      </Step>

      <Step gate="im2col">
        <H>A convolution is a matrix multiply in disguise</H>
        <P>
          Cut every 3×3 patch out of the image and lay each one flat as a row of 9 numbers. Now the convolution with a kernel is
          just that matrix of patches times the kernel, flattened into a column. Put several kernels side by side as columns,
          and one matrix multiply runs them all.
        </P>
        <Exercise
          id="im2col"
          title="patches"
          {...EX.im2col}
          hints={[
            <>
              For every top-left corner <C>(i, j)</C> with room for a k×k patch, push{' '}
              <C>{'img[i + a][j + b]'}</C> for <C>a</C> and <C>b</C> from 0 to k − 1, row by row.
            </>,
          ]}
        />
        <Callout kind="why">
          This trick, called <em>im2col</em>, is why GPUs are so good at vision: they’re built to multiply matrices, and with it
          every convolution becomes a matrix multiply. It also means you already have everything needed to train a CNN.
        </Callout>
      </Step>

      <Step gate="cnn-digits">
        <H>Train a convolutional network</H>
        <P>
          Eight kernels, learned rather than hand-designed, then one layer to make the decision. Nobody tells it to look for
          edges, but watch the learned kernel at the end.
        </P>
        <Exercise
          id="cnn-digits" timeoutMs={60000}
          title="A tiny CNN for digits"
          {...EX.cnnDigits}
          hints={[
            <>
              <C>{'const feats = Tensor.from(idx.flatMap(i => P[i])).matmul(K).add(kb).relu()'}</C>
            </>,
            <>
              <C>{'return head.forward(feats.reshape(idx.length, POS * C))'}</C>
            </>,
          ]}
        />
      </Step>

      <Step gate="q-hierarchy">
        <H>Deep convolutional networks</H>
        <P>
          Stack convolutions and the features build on each other. Early layers find edges and spots; middle layers combine
          those into textures and parts; deep layers respond to whole objects. This is how vision models from 2012’s AlexNet
          onwards learn to see.
        </P>
        <UL>
          <li>
            <strong>Stride</strong>: move the kernel more than one pixel at a time to shrink the output.
          </li>
          <li>
            <strong>Channels</strong>: a colour image has 3 (red, green, blue), and every layer outputs one channel per kernel.
            Kernels span all input channels, so a 3×3 kernel on 64 channels has 3 × 3 × 64 weights.
          </li>
          <li>
            <strong>Padding</strong>: add a border of zeros so the output keeps the input’s size.
          </li>
        </UL>
        <Quiz
          id="q-hierarchy"
          question="Why do deeper layers respond to bigger, more complex things?"
          options={[
            { label: 'Each layer sees a patch of the layer below, which already summarised patches — so its view of the original image keeps growing', correct: true },
            { label: 'Deeper layers have bigger kernels', why: 'Kernels are usually the same small size (3×3) at every depth.' },
            { label: 'They get the image at a higher resolution', why: 'Usually the reverse: pooling and stride make deep feature maps smaller.' },
          ]}
          explain="That growing window is called the receptive field. Transformers for images (Module 10) take a different route: they chop the image into patches and let attention connect any patch to any other from the first layer."
        />
        <Callout kind="note">
          Convolutions aren’t just for images. Sound is a 1-D signal, and 1-D convolutions slide along it the same way — speech
          and audio models have used them for years, and some text models slide them along sequences of words.
        </Callout>
      </Step>
    </Lesson>
  )
}
